import {
  Box,
  TextField,
  Typography,
  Divider,
  Autocomplete,
  InputAdornment,
  Stack,
  Grid,
  FormControlLabel,
  Checkbox,
  Paper,
  Switch,
  Tooltip,
  IconButton,
  Button,
  CardMedia,
  Card,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import {
  Description as DescriptionIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TemplatePreview from "../../widgets/TemplatePreview";
import PageTitle from "../../widgets/PageTitle";
import BlockHolder from "../../widgets/BlockHolder";
import DateTimePickerWrapper from "../../widgets/DateTimePickerWrapper";
import SectionLabel from "../../widgets/SectionLabel";
import { API_ENDPOINT } from "../../utils/constants";
import axios from "axios";
import DeleteIcon from "@mui/icons-material/Delete";
import { getHeader, toastError } from "../../utils/util";
import LoadingWidget from "../../widgets/LoadingWidget";
import MediaSelector from "./headers/MediaSelector";
import { useDialog } from "../../context/DialogContext";
import QualityRatingDisplay from "./RdQualityRatingDisplay";
import WhatsAppTemplateValidator from "./WhatsAppTemplateValidator";
import HeaderInputs from "./getHeaderInputs";
import BodyInputs from "./getBodyInputs";
import { toast } from "react-toastify";
import CustomCircularP from "../../widgets/CustomCircularP";
import SmartRetrySettings, { DEFAULT_RETRIES } from "./SmartRetrySettings";

const RdCampaignStep1 = ({
  qualityRating,
  templateD,
  scheduledDate,
  setScheduledDate,
  campaignPost,
  setCampaignPost,
}) => {
  const [inputValue, setInputValue] = useState("");
  const { showMediaDialog } = useDialog();
  const [templateLoading, setTemplateLoading] = useState(false);
  const [checkingDefault, setCheckingDefault] = useState(false);

  useEffect(() => {
    if (campaignPost.template) {
      const bodyComponent = campaignPost.template.components?.find(
        (c) => c.type === "BODY"
      );
      const carouselItem = campaignPost.template.components.find(
        (item) => item.type === "CAROUSEL"
      );
      const isCarousel = !!carouselItem;
      const carouselMediaCount = carouselItem?.cards?.length || 0;
      const carouselMediaType =
        carouselItem?.cards?.[0]?.components?.[0]?.format?.toLowerCase();
      const exampleTextLength = bodyComponent?.example?.body_text?.[0]?.length;

      setCampaignPost((prev) => {
        // Only update if the length is different from current bodyParams
        const carouselO = isCarousel ? { isCarousel, carouselMediaCount } : {};
        if (
          exampleTextLength !== undefined &&
          prev.bodyParams?.length !== exampleTextLength
        ) {
          return {
            ...prev,
            ...carouselO,
            bodyParams: new Array(exampleTextLength).fill(""),
          };
        } else if (
          exampleTextLength === undefined &&
          prev.bodyParams?.length !== 0
        ) {
          return {
            ...prev,
            ...carouselO,
            bodyParams: [],
          };
        }
        return { ...prev, ...carouselO }; // no change needed
      });
    }
  }, [campaignPost.template, setCampaignPost]);

  const buttonComponent = useMemo(() => {
    const components = campaignPost.template?.components;
    if (!Array.isArray(components)) {
      return null;
    }

    const buttons = components.find((c) => c.type === "BUTTONS");
    return Array.isArray(buttons?.buttons) ? buttons : null;
  }, [campaignPost.template]);

  const isButtonUrlParams = useMemo(() => {
    return (
      buttonComponent?.buttons.some(
        (button) =>
          button.type.toLowerCase() === "url" && button.url?.includes("{{")
      ) ?? false
    );
  }, [buttonComponent]);

  const isButtonCatalog = useMemo(() => {
    return (
      buttonComponent?.buttons.some(
        (button) => button.type.toLowerCase() === "catalog"
      ) ?? false
    );
  }, [buttonComponent]);

  const handleTemplateLoading = useCallback(
    async (template) => {
      if (!template) {
        setCampaignPost((prev) => ({ ...prev, template: null }));
        return;
      }
      try {
        setTemplateLoading(true);
        const response = await axios.get(
          `${API_ENDPOINT}/api/get-template/${template.id}`,
          getHeader()
        );
        setCampaignPost((prev) => ({
          campaignName: campaignPost.campaignName,
          template: response.data,
          header: {},
          bodyParams: [],
          contacts: [],
          smartRetry: false, // Initialize smartRetry
        }));
      } catch (error) {
        toastError(error);
      } finally {
        setTemplateLoading(false);
      }
    },
    [campaignPost, setCampaignPost]
  );

  const handleCampaignNameChange = (e) => {
    setCampaignPost((prev) => ({
      ...prev,
      campaignName: e.target.value,
    }));
  };

  async function checkDefaultMedia(templateId) {
    try {
      setCheckingDefault(true);
      const response = await axios.get(
        `${API_ENDPOINT}/api/check-template-cards/${templateId}`,
        getHeader()
      );
      toast.success(response.data.message);
    } catch (error) {
      setCampaignPost((prev) => ({
        ...prev,
        useDefaultMedia: false,
      }));
      toastError(error);
    } finally {
      setCheckingDefault(false);
    }
  }

  const handleBodyChange = (index, value) => {
    const newBodyParams = [...campaignPost.bodyParams];
    newBodyParams[index] = value;
    setCampaignPost((prev) => ({ ...prev, bodyParams: newBodyParams }));
  };

  const selectMedia = useCallback(
    (mode) => {
      setCampaignPost((prev) => ({
        ...prev,
        cards: mode,
      }));
    },
    [setCampaignPost]
  );

  const minDate = new Date();

  return (
    <Box sx={{ mt: 3 }}>
      <CustomCircularP show={checkingDefault} />
      <Grid container>
        <Grid item xs={12} md={7}>
          <PageTitle>Campaign Details</PageTitle>
          {qualityRating && (
            <QualityRatingDisplay qualityRating={qualityRating} />
          )}
          <Divider sx={{ mb: 3, mt: 2 }} />

          <Stack mb={2} spacing={4} maxWidth={"560px"} width={"100%"}>
            <TextField
              size="small"
              fullWidth
              label="Campaign Name"
              name="campaignName"
              value={campaignPost.campaignName}
              onChange={handleCampaignNameChange}
              required
              variant="outlined"
              InputProps={{
                sx: {
                  borderRadius: "8px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                },
              }}
            />
            <Autocomplete
              size="small"
              options={templateD}
              getOptionLabel={(option) => option.name}
              onChange={async (event, template) => {
                handleTemplateLoading(template);
              }}
              value={
                templateD.find((t) => t.id === campaignPost?.template?.id) ||
                null
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Template"
                  variant="outlined"
                  placeholder="Type template name..."
                  fullWidth
                  required
                  error={templateD.isError}
                  helperText={templateD.error}
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      borderRadius: "8px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    },
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: <>{params.InputProps.endAdornment}</>,
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ py: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <DescriptionIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle1">{option.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        ID: {option.id}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              )}
              sx={{
                "& .MuiAutocomplete-popper": {
                  boxShadow: 2,
                  borderRadius: "8px",
                  mt: 1,
                },
              }}
              noOptionsText={
                inputValue
                  ? "No matching templates found"
                  : "No templates available"
              }
            />
          </Stack>
          {campaignPost?.template && (
            <Box>
              <BlockHolder mt={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(campaignPost?.isCampaignScheduled)}
                      onChange={(e) =>
                        setCampaignPost((prev) => ({
                          ...prev,
                          isCampaignScheduled: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Is campaign scheduled?"
                  sx={{ mb: 1 }}
                />
                {Boolean(campaignPost?.isCampaignScheduled) && (
                  <DateTimePickerWrapper
                    value={scheduledDate}
                    onChange={setScheduledDate}
                    minDate={minDate}
                    format="dd/MM/yyyy h:mm a"
                    disableClock={true}
                  />
                )}
              </BlockHolder>
              {campaignPost?.template && (
                <HeaderInputs
                  template={campaignPost.template}
                  header={campaignPost?.header ?? {}}
                  onHeaderChange={(newV) => {
                    setCampaignPost((prev) => ({
                      ...prev,
                      header: {
                        ...prev.header,
                        ...newV,
                      },
                    }));
                  }}
                />
              )}

              {campaignPost.bodyParams.length > 0 ? (
                <BlockHolder>
                  <SectionLabel color={"GrayText"}>
                    This template has {campaignPost.bodyParams.length}{" "}
                    parameters
                  </SectionLabel>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={Boolean(campaignPost?.isStaticParams)}
                        onChange={(e) =>
                          setCampaignPost((prev) => ({
                            ...prev,
                            isStaticParams: e.target.checked,
                          }))
                        }
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <span>Is your parameters static?</span>
                        <Tooltip
                          title="Static parameters means the same parameter values will be used for all target numbers in this campaign"
                          placement="right"
                          arrow
                        >
                          <IconButton size="small" sx={{ ml: 1 }}>
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  />
                </BlockHolder>
              ) : (
                <SectionLabel mt={2} color={"GrayText"}>
                  This template has NO parameters
                </SectionLabel>
              )}
              {campaignPost.bodyParams.length > 0 &&
                campaignPost?.isStaticParams && (
                  <BodyInputs
                    bodyParams={campaignPost.bodyParams}
                    onBodyChange={handleBodyChange}
                  />
                )}
              {isButtonUrlParams && (
                <BlockHolder>
                  <SectionLabel>Url Param value</SectionLabel>
                  <TextField
                    sx={{ my: 2 }}
                    fullWidth
                    label="Enter url button param value"
                    size="small"
                    value={campaignPost?.urlParams}
                    onChange={(e) =>
                      setCampaignPost((prev) => ({
                        ...prev,
                        urlParams: e.target.value,
                      }))
                    }
                  />
                </BlockHolder>
              )}
              {isButtonCatalog && (
                <BlockHolder>
                  <SectionLabel>
                    Thumbnail product retailer id(optional)
                  </SectionLabel>
                  <TextField
                    sx={{ my: 2 }}
                    fullWidth
                    label="SKU number"
                    helperText="Item SKU number. Labeled as Content ID in the Commerce Manager."
                    size="small"
                    value={campaignPost?.catalogueThumbnail}
                    onChange={(e) =>
                      setCampaignPost((prev) => ({
                        ...prev,
                        catalogueThumbnail: e.target.value,
                      }))
                    }
                  />
                </BlockHolder>
              )}
              <>
                {campaignPost.isCarousel && (
                  <Box sx={{ mb: 3 }}>
                    <SectionLabel mt={2} color="GrayText">
                      This template is carousel and it has{" "}
                      {campaignPost.carouselMediaCount} media items
                    </SectionLabel>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Button
                        disabled={campaignPost.useDefaultMedia}
                        onClick={() => {
                          showMediaDialog(
                            "Select Media",
                            `Please select the media you want to use`,
                            {
                              isRcs: false,
                              mediaType: "",
                              selection: "multi",
                              selectionLimit: campaignPost.carouselMediaCount,
                              onConfirm: selectMedia,
                            }
                          );
                        }}
                        variant="outlined"
                        startIcon={<PermMediaIcon />}
                        sx={{ mb: 2 }}
                      >
                        Select card media
                      </Button>

                      <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!campaignPost?.useDefaultMedia}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                if (checked) {
                                  checkDefaultMedia(campaignPost.template.id);
                                }
                                setCampaignPost((prev) => ({
                                  ...prev,
                                  useDefaultMedia: checked,
                                }));
                              }}
                              color="primary"
                            />
                          }
                          label="Use default media"
                        />
                      </Box>
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 4 }}
                    >
                      Default media available for templates created on our
                      platform.
                    </Typography>

                    {/* Horizontal Scrollable Media Preview */}
                    {campaignPost?.cards?.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          overflowX: "auto",
                          gap: 2,
                          pb: 2,
                          "&::-webkit-scrollbar": {
                            height: "8px",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "rgba(0,0,0,0.2)",
                            borderRadius: "4px",
                          },
                        }}
                      >
                        {campaignPost.cards.map((media) => (
                          <Card
                            key={media.id}
                            sx={{
                              flex: "0 0 auto",
                              width: 200,
                              height: 200,
                              borderRadius: 2,
                              boxShadow: 3,
                              overflow: "hidden",
                              position: "relative", // For positioning the delete button
                            }}
                          >
                            <CardMedia
                              component={
                                media.mimeType.startsWith("image")
                                  ? "img"
                                  : "video"
                              }
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: media.mimeType.startsWith("image")
                                  ? "cover"
                                  : "contain",
                              }}
                              src={media.previewUrl}
                              alt={media.fileName}
                              controls={media.mimeType.startsWith("video")}
                            />
                          </Card>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </>

              {/* Input fields */}
              {campaignPost.template && (
                <WhatsAppTemplateValidator template={campaignPost.template} />
              )}

              {/* Smart Retry Feature */}
              <BlockHolder sx={{ mt: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={campaignPost.smartRetry || false}
                      onChange={(e) =>
                        setCampaignPost((prev) => ({
                          ...prev,
                          smartRetry: e.target.checked,
                        }))
                      }
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="body1">
                        Enable Smart Retry for Failed Messages
                      </Typography>
                      <Tooltip
                        title={
                          <Box sx={{ p: 1, maxWidth: 350 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: "bold",
                                mb: 1,
                                color: "#ff9800",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              ⚡ Smart Retry Algorithm
                            </Typography>
                            <Box
                              component="ol"
                              sx={{
                                pl: 2,
                                m: 0,
                                "& li": {
                                  mb: 1.5,
                                  pl: 0.5,
                                  position: "relative",
                                },
                              }}
                            >
                              <li>
                                <Box
                                  component="span"
                                  sx={{ fontWeight: "medium" }}
                                >
                                  Initial Attempt:
                                </Box>{" "}
                                Send message →{" "}
                                <Box
                                  component="span"
                                  sx={{ color: "success.main" }}
                                >
                                  If success → Stop
                                </Box>
                              </li>
                              <li>
                                <Box
                                  component="span"
                                  sx={{ fontWeight: "medium" }}
                                >
                                  If failed → Wait 2 hours → Retry
                                </Box>
                              </li>
                              <li>
                                <Box
                                  component="span"
                                  sx={{ fontWeight: "medium" }}
                                >
                                  If failed again → Wait 4 hours → Retry
                                </Box>
                              </li>
                              <li>
                                <Box
                                  component="span"
                                  sx={{ fontWeight: "medium" }}
                                >
                                  If failed again → Wait 24 hours → Final retry
                                </Box>
                              </li>
                              <li>
                                <Box
                                  component="span"
                                  sx={{
                                    fontWeight: "medium",
                                    color: "error.main",
                                  }}
                                >
                                  If still failed → Mark as permanently failed
                                </Box>
                              </li>
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mt: 2,
                                color: "text.secondary",
                                fontStyle: "italic",
                              }}
                            >
                              This feature will automatically retry failed
                              messages with increasing delays to maximize
                              delivery success.
                            </Typography>
                          </Box>
                        }
                        placement="right"
                        arrow
                        enterDelay={300}
                        leaveDelay={200}
                      >
                        <IconButton size="small" sx={{ ml: 1 }}>
                          <InfoOutlinedIcon fontSize="small" color="primary" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                />
                {campaignPost.smartRetry && (
                  <SmartRetrySettings
                    value={campaignPost.retryIntervals ?? DEFAULT_RETRIES}
                    onChange={(v) => {
                      console.log(v);
                      setCampaignPost((p) => ({ ...p, retryIntervals: v }));
                    }}
                  />
                )}
                {campaignPost.smartRetry && (
                  <Typography
                    variant="caption"
                    color="success.main"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      ml: 4,
                      mt: 1,
                      fontWeight: "medium",
                      backgroundColor: "rgba(76, 175, 80, 0.1)",
                      p: 1,
                      borderRadius: 1,
                      width: "fit-content",
                    }}
                  >
                    ⚡ Smart Retry is active. Failed messages will be
                    automatically retried with increasing delays.
                  </Typography>
                )}
              </BlockHolder>
            </Box>
          )}
        </Grid>
        <Grid item xs={12} md={5}>
          {campaignPost?.template ? (
            <Box
              border={
                campaignPost.template.status !== "APPROVED"
                  ? `2px solid red`
                  : `none`
              }
              sx={{ borderRadius: 1 }}
            >
              {campaignPost.template.status !== "APPROVED" && (
                <PageTitle color="red" ml={2}>
                  {campaignPost.template.status}
                </PageTitle>
              )}
              <TemplatePreview
                template={campaignPost.template}
                previewUrl={campaignPost.header?.mediaUrl ?? ""}
                fileName={campaignPost?.header?.filename ?? ""}
              />
            </Box>
          ) : templateLoading ? (
            <LoadingWidget
              label="Loading Template..."
              thickness={6}
              type="circular"
            />
          ) : (
            <Typography variant="h6" m={3} color={"GrayText"}>
              Please select template to preview
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default RdCampaignStep1;
